/**
 * Service — Paramétrage de l'organisation RH
 *
 * ── Stratégie de persistance ────────────────────────────────────────────────
 *
 * Ce service expose une API **synchrone** (legacy) lisant/écrivant dans
 * localStorage. Pour la migration progressive vers le backend, on ajoute :
 *
 *  1. `init()` — synchronise le cache localStorage avec le bundle backend
 *     au démarrage (à appeler depuis AuthContext après login).
 *
 *  2. Les méthodes `add/update/delete` appliquent l'écriture localement
 *     **puis** propagent au backend en *fire-and-forget* (catch silencieux
 *     avec console.warn). En cas d'échec réseau, la donnée reste cohérente
 *     en cache et sera resynchronisée au prochain `init()`.
 *
 *  3. `migrateLocalToBackend()` — bulk import du contenu localStorage actuel
 *     vers le backend (`POST /rh/organisation/import-localstorage`). À
 *     appeler une seule fois pour les utilisateurs existants (flag
 *     `organisation_migrated` en localStorage).
 *
 * ── Multi-tenant ────────────────────────────────────────────────────────────
 *
 * La structure organisationnelle (directions, départements, services,
 * fonctions, postes, grilles salariales, catégories) est SEGMENTÉE PAR
 * ENTITÉ. Chaque entité (CGE-GUI, autre patronat, etc.) a sa propre
 * arborescence isolée. La clé localStorage est dérivée de l'entiteId du
 * user connecté : `rh_organisation_<entiteId>`. Côté backend, le filtrage
 * par `entiteId` est appliqué automatiquement via le JWT.
 *
 * Les super_admin (qui n'ont pas d'entiteId — ils voient TOUTES les entités
 * en théorie) tombent sur la clé legacy `rh_organisation` (= partagée).
 */

import {
  OrganisationData, Direction, Departement, Service,
  Categorie, Fonction, Poste, GrilleSalaire, NoeudOrganisation,
  posteVide
} from '../types/organisation.types';
import { authService } from './auth.service';
import { organisationApi, ImportLocalStorageSummary } from './organisationApi.service';

const KEY_LEGACY = 'rh_organisation';

/**
 * Calcule la clé localStorage en fonction de l'utilisateur courant.
 * Pour un user d'entité : `rh_organisation_<entiteId>`
 * Pour un super_admin (pas d'entité) : `rh_organisation` (legacy partagée)
 * Si pas connecté : fallback legacy.
 */
function getStorageKey(): string {
  const user = authService.getUser();
  const entiteId = user?.entiteId;
  if (!entiteId) return KEY_LEGACY;
  return `${KEY_LEGACY}_${entiteId}`;
}

// ── Données par défaut ──────────────────────────────────────────────────────

const defaults: OrganisationData = {
  directions: [
    { id: 'dir1', code: 'DG',  libelle: 'Direction Générale',                    actif: true },
    { id: 'dir2', code: 'DAF', libelle: 'Direction Administrative & Financière', actif: true },
    { id: 'dir3', code: 'DRH', libelle: 'Direction des Ressources Humaines',     actif: true },
    { id: 'dir4', code: 'DCO', libelle: 'Direction Commerciale',                 actif: true },
    { id: 'dir5', code: 'DOP', libelle: 'Direction des Opérations',              actif: true },
  ],
  departements: [
    { id: 'dep1', code: 'DEP-FIN',  libelle: 'Finance & Comptabilité',  directionId: 'dir2', actif: true },
    { id: 'dep2', code: 'DEP-RH',   libelle: 'Ressources Humaines',      directionId: 'dir3', actif: true },
    { id: 'dep3', code: 'DEP-COM',  libelle: 'Commercial & Ventes',      directionId: 'dir4', actif: true },
    { id: 'dep4', code: 'DEP-LOG',  libelle: 'Logistique',               directionId: 'dir5', actif: true },
    { id: 'dep5', code: 'DEP-INF',  libelle: 'Informatique',             directionId: 'dir5', actif: true },
  ],
  services: [
    { id: 'srv1', code: 'SRV-CPT',  libelle: 'Comptabilité',            departementId: 'dep1', actif: true },
    { id: 'srv2', code: 'SRV-TRE',  libelle: 'Trésorerie',              departementId: 'dep1', actif: true },
    { id: 'srv3', code: 'SRV-PAI',  libelle: 'Paie & Administration',   departementId: 'dep2', actif: true },
    { id: 'srv4', code: 'SRV-REC',  libelle: 'Recrutement & Formation', departementId: 'dep2', actif: true },
    { id: 'srv5', code: 'SRV-VTE',  libelle: 'Ventes',                  departementId: 'dep3', actif: true },
  ],
  categories: [
    { id: 'cat1', code: '1',    libelle: 'Ouvrier / Manœuvre',           actif: true },
    { id: 'cat2', code: '2',    libelle: 'Technicien / Employé qualifié', actif: true },
    { id: 'cat3', code: 'AM',   libelle: 'Agent de Maîtrise',            actif: true },
    { id: 'cat4', code: 'CAD',  libelle: 'Cadre',                        actif: true },
    { id: 'cat5', code: 'CSU',  libelle: 'Cadre Supérieur',              actif: true },
    { id: 'cat6', code: 'DIR',  libelle: 'Directeur',                    actif: true },
  ],
  fonctions: [
    { id: 'fn1', code: 'DG',    libelle: 'Directeur Général',          niveauHierarchique: 1, actif: true },
    { id: 'fn2', code: 'DGA',   libelle: 'Directeur Général Adjoint',  niveauHierarchique: 1, actif: true },
    { id: 'fn3', code: 'DDIR',  libelle: 'Directeur de Direction',     niveauHierarchique: 2, actif: true },
    { id: 'fn4', code: 'CDEP',  libelle: 'Chef de Département',        niveauHierarchique: 2, actif: true },
    { id: 'fn5', code: 'CSRV',  libelle: 'Chef de Service',            niveauHierarchique: 3, actif: true },
    { id: 'fn6', code: 'RRH',   libelle: 'Responsable RH',             niveauHierarchique: 3, actif: true },
    { id: 'fn7', code: 'CPT',   libelle: 'Comptable',                  niveauHierarchique: 4, actif: true },
    { id: 'fn8', code: 'COM',   libelle: 'Commercial',                 niveauHierarchique: 4, actif: true },
    { id: 'fn9', code: 'CAI',   libelle: 'Caissier',                   niveauHierarchique: 5, actif: true },
    { id: 'fn10', code: 'CHAU', libelle: 'Chauffeur',                  niveauHierarchique: 5, actif: true },
    { id: 'fn11', code: 'SEC',  libelle: 'Agent de Sécurité',          niveauHierarchique: 5, actif: true },
  ],
  postes: [
    {
      ...posteVide(),
      id: 'pst1', code: 'P-DG', libelle: 'Directeur Général',
      fonctionId: 'fn1', categorieId: 'cat6', directionId: 'dir1',
      missionsClefs: ['Définir la stratégie', 'Représenter l\'entreprise', 'Piloter les directions'],
      competencesRequises: ['Leadership', 'Vision stratégique', 'Gestion P&L'],
      avantageIds: [], grilleSalaireId: '', actif: true,
    },
    {
      ...posteVide(),
      id: 'pst2', code: 'P-RRH', libelle: 'Responsable Ressources Humaines',
      fonctionId: 'fn6', categorieId: 'cat4', departementId: 'dep2',
      missionsClefs: ['Gestion paie & administration', 'Recrutement', 'Formation', 'Relations sociales'],
      competencesRequises: ['Droit du travail', 'SIRH', 'Gestion des conflits'],
      avantageIds: [], grilleSalaireId: '', actif: true,
    },
    {
      ...posteVide(),
      id: 'pst3', code: 'P-CPT', libelle: 'Comptable Senior',
      fonctionId: 'fn7', categorieId: 'cat3', serviceId: 'srv1',
      missionsClefs: ['Saisie comptable', 'Rapprochements bancaires', 'États financiers'],
      competencesRequises: ['Comptabilité OHADA', 'Excel', 'Logiciel comptable'],
      avantageIds: [], grilleSalaireId: '', actif: true,
    },
  ],
  grilles: [
    {
      id: 'grille1',
      libelle: 'Grille Convention Collective 2025',
      description: 'Barème salarial mensuel — convention collective interprofessionnelle',
      typeRemuneration: 'mensuel' as const,
      joursParMois: 26,
      statut: 'active' as const,
      dateEffet: '2025-01-01',
      primesGlobales: [
        { id: 'pg1', libelle: 'Prime de présence',    montant: 10000,  periodicite: 'mensuelle'     },
        { id: 'pg2', libelle: 'Prime de rendement',   montant: 25000,  periodicite: 'trimestrielle' },
        { id: 'pg3', libelle: 'Prime de fin d\'année', montant: 100000, periodicite: 'annuelle'     },
      ],
      echelons: [
        { niveau: 'N1', echelon: 'A', coefficient: 100, salaireBase: 80000,  sursalaire: 0,     indemniteTransport: 15000 },
        { niveau: 'N1', echelon: 'B', coefficient: 120, salaireBase: 95000,  sursalaire: 5000,  indemniteTransport: 15000 },
        { niveau: 'N2', echelon: 'A', coefficient: 150, salaireBase: 120000, sursalaire: 0,     indemniteTransport: 20000 },
        { niveau: 'N2', echelon: 'B', coefficient: 180, salaireBase: 150000, sursalaire: 10000, indemniteTransport: 20000 },
        { niveau: 'N3', echelon: 'A', coefficient: 220, salaireBase: 200000, sursalaire: 0,     indemniteTransport: 25000 },
        { niveau: 'N3', echelon: 'B', coefficient: 260, salaireBase: 250000, sursalaire: 15000, indemniteTransport: 25000 },
        { niveau: 'N4', echelon: 'A', coefficient: 320, salaireBase: 350000, sursalaire: 0,     indemniteTransport: 30000 },
        { niveau: 'N4', echelon: 'B', coefficient: 380, salaireBase: 450000, sursalaire: 20000, indemniteTransport: 30000 },
        { niveau: 'N5', echelon: 'A', coefficient: 450, salaireBase: 600000, sursalaire: 0,     indemniteTransport: 35000 },
        { niveau: 'N5', echelon: 'B', coefficient: 550, salaireBase: 800000, sursalaire: 50000, indemniteTransport: 35000 },
      ],
      actif: true,
    },
    {
      id: 'grille_journaliers',
      libelle: 'Grille Journaliers 2025',
      description: 'Barème journalier — travailleurs journaliers et saisonniers (base 26 jours/mois)',
      typeRemuneration: 'journalier' as const,
      joursParMois: 26,
      statut: 'active' as const,
      dateEffet: '2025-01-01',
      primesGlobales: [
        { id: 'pjg1', libelle: 'Prime de présence journalière', montant: 500, periodicite: 'mensuelle' },
      ],
      echelons: [
        // Manœuvre / Ouvrier non qualifié
        { niveau: 'J1', echelon: 'A', coefficient: 100, salaireBase: 0, tauxJournalier: 3500,  sursalaireJournalier: 0,   indemniteTransportJournaliere: 600,  heuresParJour: 8 },
        { niveau: 'J1', echelon: 'B', coefficient: 110, salaireBase: 0, tauxJournalier: 3850,  sursalaireJournalier: 200, indemniteTransportJournaliere: 600,  heuresParJour: 8 },
        // Ouvrier qualifié
        { niveau: 'J2', echelon: 'A', coefficient: 130, salaireBase: 0, tauxJournalier: 4500,  sursalaireJournalier: 0,   indemniteTransportJournaliere: 700,  heuresParJour: 8 },
        { niveau: 'J2', echelon: 'B', coefficient: 150, salaireBase: 0, tauxJournalier: 5200,  sursalaireJournalier: 300, indemniteTransportJournaliere: 700,  heuresParJour: 8 },
        // Technicien / Spécialisé
        { niveau: 'J3', echelon: 'A', coefficient: 180, salaireBase: 0, tauxJournalier: 6500,  sursalaireJournalier: 0,   indemniteTransportJournaliere: 800,  heuresParJour: 8 },
        { niveau: 'J3', echelon: 'B', coefficient: 210, salaireBase: 0, tauxJournalier: 7500,  sursalaireJournalier: 500, indemniteTransportJournaliere: 800,  heuresParJour: 8 },
        // Chef d'équipe
        { niveau: 'J4', echelon: 'A', coefficient: 250, salaireBase: 0, tauxJournalier: 9500,  sursalaireJournalier: 0,   indemniteTransportJournaliere: 1000, heuresParJour: 8 },
        { niveau: 'J4', echelon: 'B', coefficient: 290, salaireBase: 0, tauxJournalier: 11000, sursalaireJournalier: 800, indemniteTransportJournaliere: 1000, heuresParJour: 8 },
      ],
      actif: true,
    },
    {
      id: 'grille0',
      libelle: 'Grille Convention Collective 2024',
      description: 'Barème salarial mensuel — exercice 2024 (archivé)',
      typeRemuneration: 'mensuel' as const,
      joursParMois: 26,
      statut: 'archivee' as const,
      dateEffet: '2024-01-01',
      dateFinValidite: '2024-12-31',
      grilleRemplaceeId: undefined,
      primesGlobales: [
        { id: 'pg0a', libelle: 'Prime de présence',  montant: 8000,  periodicite: 'mensuelle'     },
        { id: 'pg0b', libelle: 'Prime de rendement', montant: 20000, periodicite: 'trimestrielle' },
      ],
      echelons: [
        { niveau: 'N1', echelon: 'A', coefficient: 100, salaireBase: 75000,  indemniteTransport: 12000 },
        { niveau: 'N1', echelon: 'B', coefficient: 120, salaireBase: 88000,  indemniteTransport: 12000 },
        { niveau: 'N2', echelon: 'A', coefficient: 150, salaireBase: 110000, indemniteTransport: 18000 },
        { niveau: 'N2', echelon: 'B', coefficient: 180, salaireBase: 140000, indemniteTransport: 18000 },
        { niveau: 'N3', echelon: 'A', coefficient: 220, salaireBase: 185000, indemniteTransport: 22000 },
        { niveau: 'N3', echelon: 'B', coefficient: 260, salaireBase: 235000, indemniteTransport: 22000 },
      ],
      actif: false,
    },
  ],
};

// ── Service ─────────────────────────────────────────────────────────────────

export const organisationService = {

  // ── Lecture / écriture ──────────────────────────────────────────────────

  get(): OrganisationData {
    try {
      const key = getStorageKey();
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed: Partial<OrganisationData> = JSON.parse(raw);
        return {
          directions:   parsed.directions   ?? defaults.directions,
          departements: parsed.departements ?? defaults.departements,
          services:     parsed.services     ?? defaults.services,
          categories:   parsed.categories   ?? defaults.categories,
          fonctions:    parsed.fonctions    ?? defaults.fonctions,
          postes:       parsed.postes       ?? defaults.postes,
          grilles:      parsed.grilles      ?? defaults.grilles,
        };
      }
      // Filtrage STRICT par entité : chaque entité démarre avec les défauts
      // génériques (directions/services seedés, grilles types) et possède son
      // propre stockage isolé. Pas de migration depuis la legacy partagée :
      // les modifications d'une entité ne pollueront jamais les autres, et
      // les grilles salariales sont strictement spécifiques à chaque entité.
      //
      // Note : si l'utilisateur a saisi des grilles dans l'ancienne base
      // partagée avant la segmentation, elles restent accessibles via la clé
      // legacy `rh_organisation` (récupérables manuellement si besoin via les
      // outils de dev navigateur). Une fonction d'import explicite pourra
      // être ajoutée plus tard si la demande émerge.
    } catch {}
    return { ...defaults };
  },

  save(data: OrganisationData): void {
    localStorage.setItem(getStorageKey(), JSON.stringify(data));
  },

  // ════════════════════════════════════════════════════════════════════════
  // Import depuis une autre organisation
  // ════════════════════════════════════════════════════════════════════════
  //
  // Permet de récupérer la structure organisationnelle d'une autre entité
  // (directions, départements, services, fonctions, catégories, postes,
  // grilles salariales) après la mise en place du filtrage multi-tenant.
  //
  // Cas d'usage typique : un patronat vient d'être créé et reprend la grille
  // salariale standard d'un autre patronat similaire, ou d'une "base partagée"
  // (clé legacy `rh_organisation`).

  /**
   * Liste les sources possibles d'import : toutes les clés localStorage qui
   * commencent par `rh_organisation_` (= entités qui ont déjà des données),
   * plus la base partagée legacy si elle existe.
   *
   * Retourne un libellé humain (ID entité ou "Base partagée") + la clé
   * réelle pour l'import.
   */
  listAvailableSources(): Array<{ key: string; label: string; entiteId: string | null; nbItems: number }> {
    const sources: Array<{ key: string; label: string; entiteId: string | null; nbItems: number }> = [];
    const currentKey = getStorageKey();

    // Parcourt toutes les clés localStorage à la recherche de stockages org
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (k !== KEY_LEGACY && !k.startsWith(`${KEY_LEGACY}_`)) continue;
      // Ne pas se proposer soi-même comme source
      if (k === currentKey) continue;

      try {
        const parsed = JSON.parse(localStorage.getItem(k) || '{}') as Partial<OrganisationData>;
        const nbItems = (parsed.directions?.length || 0)
          + (parsed.departements?.length || 0)
          + (parsed.services?.length || 0)
          + (parsed.categories?.length || 0)
          + (parsed.fonctions?.length || 0)
          + (parsed.postes?.length || 0)
          + (parsed.grilles?.length || 0);
        const entiteId = k === KEY_LEGACY ? null : k.replace(`${KEY_LEGACY}_`, '');
        sources.push({
          key: k,
          label: entiteId ? `Entité ${entiteId.slice(0, 8)}…` : 'Base partagée (legacy)',
          entiteId,
          nbItems,
        });
      } catch { /* ignore */ }
    }
    return sources;
  },

  /** Sections importables. */
  IMPORT_SECTIONS: ['directions', 'departements', 'services', 'categories', 'fonctions', 'postes', 'grilles'] as const,

  /**
   * Importe une ou plusieurs sections depuis une autre organisation vers
   * l'organisation courante.
   *
   * @param sourceKey Clé localStorage source (cf. listAvailableSources)
   * @param sections  Sections à importer (sous-ensemble de IMPORT_SECTIONS)
   * @param mode      'replace' = écrase la section, 'append' = ajoute en évitant
   *                  les doublons par `id`. Défaut : append.
   * @returns         Nombre d'éléments effectivement importés par section
   */
  importFrom(
    sourceKey: string,
    sections: ReadonlyArray<keyof OrganisationData>,
    mode: 'replace' | 'append' = 'append',
  ): { importedCounts: Record<string, number>; total: number } {
    const raw = localStorage.getItem(sourceKey);
    if (!raw) {
      throw new Error(`Source d'import introuvable : ${sourceKey}`);
    }
    const sourceData = JSON.parse(raw) as Partial<OrganisationData>;
    const currentData = this.get();
    const importedCounts: Record<string, number> = {};
    let total = 0;

    for (const section of sections) {
      const sourceItems = (sourceData[section] || []) as Array<{ id: string }>;
      if (mode === 'replace') {
        // Écrase la section entière
        (currentData as any)[section] = [...sourceItems];
        importedCounts[section] = sourceItems.length;
        total += sourceItems.length;
      } else {
        // Ajoute en évitant les doublons par id (les ids existants sont préservés)
        const existingIds = new Set(((currentData as any)[section] || []).map((x: any) => x.id));
        const toAdd = sourceItems.filter(x => !existingIds.has(x.id));
        (currentData as any)[section] = [...((currentData as any)[section] || []), ...toAdd];
        importedCounts[section] = toAdd.length;
        total += toAdd.length;
      }
    }

    this.save(currentData);
    return { importedCounts, total };
  },

  // ── Helpers CRUD génériques ─────────────────────────────────────────────

  // Génère un vrai UUID v4 (compatible avec @IsUUID() côté backend).
  // Fallback timestamp+random uniquement pour navigateurs ultra-anciens.
  _newId: () =>
    (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
      ? crypto.randomUUID()
      : Date.now().toString() + Math.random().toString(36).slice(2, 6),

  /**
   * Crée un élément. **Backend = source de vérité** :
   *  1. AWAIT l'appel API (POST). En cas d'échec → throw (l'UI affiche un toast.error)
   *     et le cache localStorage n'est PAS modifié (pas de faux succès).
   *  2. Au succès, on persiste dans le cache l'objet RENVOYÉ par le backend
   *     (il porte l'`id` serveur définitif) et on le retourne.
   */
  async add<T extends { id: string }>(section: keyof OrganisationData, item: Omit<T, 'id'>): Promise<T> {
    // Génère un id côté client (envoyé au backend, qui peut l'accepter tel quel)
    const localItem = { ...item, id: this._newId() } as T;
    const saved = (await createSectionItemOnBackend(section, localItem)) as T;
    const data = this.get();
    (data[section] as unknown as T[]).push(saved);
    this.save(data);
    return saved;
  },

  /**
   * Met à jour un élément. **Backend = source de vérité** : AWAIT le PATCH,
   * throw en cas d'échec (pas d'écriture cache), puis MAJ du cache avec l'objet
   * renvoyé par le backend.
   */
  async update<T extends { id: string }>(section: keyof OrganisationData, id: string, patch: Partial<T>): Promise<T> {
    const saved = (await updateSectionItemOnBackend(section, id, patch)) as T;
    const data = this.get();
    const arr = data[section] as unknown as T[];
    const idx = arr.findIndex(i => i.id === id);
    // Le backend peut renvoyer l'objet complet ; sinon on fusionne le patch.
    const merged = (saved && (saved as any).id)
      ? { ...(idx !== -1 ? arr[idx] : {}), ...saved }
      : { ...(idx !== -1 ? arr[idx] : ({ id } as any)), ...patch };
    if (idx !== -1) arr[idx] = merged as T;
    this.save(data);
    return merged as T;
  },

  /**
   * Supprime un élément. **Backend = source de vérité** : AWAIT le DELETE,
   * throw en cas d'échec (pas de retrait du cache), puis retrait du cache.
   */
  async delete(section: keyof OrganisationData, id: string): Promise<void> {
    await deleteSectionItemOnBackend(section, id);
    const data = this.get();
    (data[section] as { id: string }[]) =
      (data[section] as { id: string }[]).filter(i => i.id !== id);
    this.save(data);
  },

  // ── Accesseurs directs ──────────────────────────────────────────────────

  getDirections():   Direction[]   { return this.get().directions.filter(d => d.actif); },
  getDepartements(): Departement[] { return this.get().departements.filter(d => d.actif); },
  getServices():     Service[]     { return this.get().services.filter(s => s.actif); },
  getCategories():   Categorie[]   { return this.get().categories.filter(c => c.actif); },
  getFonctions():    Fonction[]    { return this.get().fonctions.filter(f => f.actif); },
  getPostes():       Poste[]       { return this.get().postes.filter(p => p.actif); },
  getGrilles():      GrilleSalaire[] { return this.get().grilles.filter(g => g.actif); },

  getDepartementsParDirection(directionId: string): Departement[] {
    return this.getDepartements().filter(d => d.directionId === directionId);
  },

  getServicesParDepartement(departementId: string): Service[] {
    return this.getServices().filter(s => s.departementId === departementId);
  },

  // ── Arbre organisationnel ───────────────────────────────────────────────

  getArbre(): NoeudOrganisation[] {
    const data = this.get();
    return data.directions.filter(d => d.actif).map(direction => ({
      direction,
      departements: data.departements
        .filter(dep => dep.directionId === direction.id && dep.actif)
        .map(departement => ({
          departement,
          services: data.services.filter(s => s.departementId === departement.id && s.actif),
        })),
    }));
  },

  // ── Résoudre libellés ───────────────────────────────────────────────────

  libelleDirection(id: string): string {
    return this.get().directions.find(d => d.id === id)?.libelle ?? '—';
  },
  libelleDepartement(id: string): string {
    return this.get().departements.find(d => d.id === id)?.libelle ?? '—';
  },
  libelleService(id: string): string {
    return this.get().services.find(s => s.id === id)?.libelle ?? '—';
  },
  libelleCategorie(id: string): string {
    return this.get().categories.find(c => c.id === id)?.libelle ?? '—';
  },
  libelleFonction(id: string): string {
    return this.get().fonctions.find(f => f.id === id)?.libelle ?? '—';
  },

  // ── Compatibilité avec rhListesService (sync) ───────────────────────────

  /**
   * Synchronise les fonctions et catégories de l'organisation
   * vers rhListesService pour alimenter les formulaires contrats.
   */
  syncVersRhListes(): void {
    const data = this.get();
    const rhData = {
      fonctions:    data.fonctions.filter(f => f.actif).map(f => ({ id: f.id, libelle: f.libelle, actif: true })),
      categories:   data.categories.filter(c => c.actif).map(c => ({ id: c.id, libelle: c.libelle, actif: true })),
      departements: data.departements.filter(d => d.actif).map(d => ({ id: d.id, libelle: d.libelle, actif: true })),
      services:     data.services.filter(s => s.actif).map(s => ({ id: s.id, libelle: s.libelle, actif: true })),
    };
    localStorage.setItem('rh_listes_params', JSON.stringify(rhData));
  },

  // ────────────────────────────────────────────────────────────────────────
  // Synchronisation Backend
  // ────────────────────────────────────────────────────────────────────────

  /**
   * Initialise le cache localStorage à partir du backend.
   *
   * À appeler :
   *  - dans AuthContext après login réussi (fire-and-forget)
   *  - manuellement via le bouton « Synchroniser avec le serveur »
   *
   * En cas d'erreur réseau, garde le contenu localStorage tel quel
   * (mode offline). N'écrase JAMAIS les données locales avec une
   * réponse vide.
   *
   * @returns true si la synchronisation a réussi, false sinon.
   */
  async init(): Promise<boolean> {
    try {
      const bundle = await organisationApi.getBundle();
      if (!bundle) return false;
      // Sécurité : si toutes les listes sont vides, ne pas écraser le cache
      const nbItems =
        (bundle.directions?.length || 0) +
        (bundle.departements?.length || 0) +
        (bundle.services?.length || 0) +
        (bundle.categories?.length || 0) +
        (bundle.fonctions?.length || 0) +
        (bundle.postes?.length || 0) +
        (bundle.grilles?.length || 0);
      if (nbItems === 0) {
        console.warn('[organisationService.init] bundle backend vide — cache localStorage préservé');
        return false;
      }
      // Normalisation : garantir que toutes les sections existent
      const normalized: OrganisationData = {
        directions:   bundle.directions   ?? [],
        departements: bundle.departements ?? [],
        services:     bundle.services     ?? [],
        categories:   bundle.categories   ?? [],
        fonctions:    bundle.fonctions    ?? [],
        postes:       bundle.postes       ?? [],
        grilles:      bundle.grilles      ?? [],
      };
      this.save(normalized);
      this.syncVersRhListes();
      return true;
    } catch (err: any) {
      console.warn('[organisationService.init] sync backend échouée — mode offline :', err?.message || err);
      return false;
    }
  },

  /**
   * Migre le contenu localStorage actuel vers le backend (UPSERT).
   *
   * Utile pour les utilisateurs qui ont saisi des données AVANT que le
   * backend ne soit branché. À appeler une seule fois ; on stocke le flag
   * `organisation_migrated` pour ne plus afficher le bouton.
   *
   * @returns Résumé du backend ({ inserted, updated, skipped }).
   * @throws  Si l'appel API échoue (à laisser remonter pour afficher un toast).
   */
  async migrateLocalToBackend(): Promise<ImportLocalStorageSummary> {
    const data = this.get();
    const summary = await organisationApi.importLocalStorage(data);
    // Flag pour masquer le bouton à l'avenir
    try { localStorage.setItem('organisation_migrated', 'true'); } catch {}
    return summary;
  },

  /**
   * Pousse le bundle localStorage actuel vers le backend (UPSERT) SANS poser
   * le flag "organisation_migrated". À utiliser après un import bulk depuis
   * CSV/Excel/Word/OCR pour que les nouvelles entrées soient persistées
   * côté serveur. Idempotent et silencieux en cas d'échec réseau.
   */
  async pushBundleToBackend(): Promise<ImportLocalStorageSummary | null> {
    try {
      const data = this.get();
      return await organisationApi.importLocalStorage(data);
    } catch (err: any) {
      console.warn(
        '[organisationService.pushBundleToBackend] échec — mode offline :',
        err?.response?.data?.message || err?.message || err,
      );
      return null;
    }
  },

  /** Indique si la migration localStorage → backend a déjà été effectuée. */
  isMigrated(): boolean {
    try { return localStorage.getItem('organisation_migrated') === 'true'; }
    catch { return false; }
  },
};

// ────────────────────────────────────────────────────────────────────────────
// Helpers internes — propagation AWAITÉE des mutations vers le backend
// ────────────────────────────────────────────────────────────────────────────
//
// Backend = source de vérité. Les méthodes add/update/delete attendent ces
// helpers AVANT d'écrire dans le cache localStorage. En cas d'échec réseau,
// l'erreur est PROPAGÉE (throw) pour que l'UI affiche un toast.error : aucun
// faux « enregistré » n'est posé dans le cache.

/** Crée l'élément côté backend et retourne l'objet serveur (avec id définitif). */
async function createSectionItemOnBackend(
  section: keyof OrganisationData,
  item: { id: string } & Record<string, any>,
): Promise<{ id: string } & Record<string, any>> {
  switch (section) {
    case 'directions':   return organisationApi.createDirection(item as Direction);
    case 'departements': return organisationApi.createDepartement(item as Departement);
    case 'services':     return organisationApi.createService(item as Service);
    case 'categories':   return organisationApi.createCategorie(item as Categorie);
    case 'fonctions':    return organisationApi.createFonction(item as Fonction);
    case 'postes':       return organisationApi.createPoste(item as Poste);
    case 'grilles':      return organisationApi.createGrilleSalaire(item as GrilleSalaire);
    default:             throw new Error(`Section inconnue : ${String(section)}`);
  }
}

/** Met à jour l'élément côté backend et retourne l'objet serveur. */
async function updateSectionItemOnBackend(
  section: keyof OrganisationData,
  id: string,
  patch: Record<string, any>,
): Promise<{ id: string } & Record<string, any>> {
  switch (section) {
    case 'directions':   return organisationApi.updateDirection(id, patch as Partial<Direction>);
    case 'departements': return organisationApi.updateDepartement(id, patch as Partial<Departement>);
    case 'services':     return organisationApi.updateService(id, patch as Partial<Service>);
    case 'categories':   return organisationApi.updateCategorie(id, patch as Partial<Categorie>);
    case 'fonctions':    return organisationApi.updateFonction(id, patch as Partial<Fonction>);
    case 'postes':       return organisationApi.updatePoste(id, patch as Partial<Poste>);
    case 'grilles':      return organisationApi.updateGrilleSalaire(id, patch as Partial<GrilleSalaire>);
    default:             throw new Error(`Section inconnue : ${String(section)}`);
  }
}

/** Supprime l'élément côté backend (throw si l'appel échoue). */
async function deleteSectionItemOnBackend(
  section: keyof OrganisationData,
  id: string,
): Promise<void> {
  switch (section) {
    case 'directions':   return organisationApi.deleteDirection(id);
    case 'departements': return organisationApi.deleteDepartement(id);
    case 'services':     return organisationApi.deleteService(id);
    case 'categories':   return organisationApi.deleteCategorie(id);
    case 'fonctions':    return organisationApi.deleteFonction(id);
    case 'postes':       return organisationApi.deletePoste(id);
    case 'grilles':      return organisationApi.deleteGrilleSalaire(id);
    default:             throw new Error(`Section inconnue : ${String(section)}`);
  }
}
